// Export Control timeline node (app.md §6/§9) — the step-8/9 approval queue
// (File Generation), grouped by company with orange rules between groups.
// The step-11 checkpoint lives in its own Closing Confirmation node
// (ClosingConfirmationScreen).
//
// "Approve & Export" is ONE request. The API builds one file per company, writes
// the copy of record, stamps the batch on each invoice and writes every Export
// Control approval — or it validates the set, refuses, and writes nothing. The
// files come back as signed URLs to save.
//
// PORT NOTE: this used to be a three-phase dance in the browser — build the CSV
// bytes, write an export row and stamp the invoices, write the decisions one at a
// time, then deliver the files and, if no decision had landed, roll the stamps
// back so the retry didn't advance invoices whose file was never delivered.
// Point Click Care parses those bytes, so the format belongs where it can be
// versioned and tested; the whole prepare/rollback apparatus goes with it.
import { useCallback } from 'react'
import { Body1, Toast, ToastBody, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { ScreenTitle } from '../components/ScreenTitle'
import { DecisionStep, InvoiceStatus, TOASTER_ID } from '../lib/constants'
import type { Invoice } from '../lib/types'
import type { Session } from '../lib/identity'
import type { InvoiceFilter } from '../lib/filters'
import { createExport } from '../lib/data'
import { downloadUrl } from '../lib/download'
import { isApiError } from '../api/errors'
import { ApprovalScreen } from './ApprovalScreen'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' },
  sub: { color: tokens.colorNeutralForeground3 },
  // Orange rule under the step header (user preference, 2026-06-10) —
  // deliberately distinct from the blue page/timeline rules. The same rule
  // repeats between the company groups (ApprovalScreen groupByCompany).
  rule: { height: '3px', backgroundColor: '#E08A2B', borderRadius: '2px' },
})

interface Props {
  session: Session
  refreshNonce?: number
  /** Page-wide filter (header Filter button). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ExportControlScreen({ session, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const { dispatchToast } = useToastController(TOASTER_ID)

  const approveVia = useCallback(
    async (invoices: Invoice[]) => {
      try {
        const { files, invoices_advanced } = await createExport(invoices.map((i) => i.id))
        // Save every file. The stored object is the record; the download is the
        // controller's copy to hand to Point Click Care.
        for (const f of files) {
          if (f.download_url) downloadUrl(f.download_url, f.file_name)
        }
        const missing = files.filter((f) => !f.download_url).map((f) => f.file_name)
        const n = files.length
        dispatchToast(
          <Toast>
            <ToastTitle>
              {n === 1 ? '1 file' : `${n} files`} built and saved
            </ToastTitle>
            <ToastBody>
              {missing.length > 0
                ? `Couldn't download: ${missing.join(', ')}. They are stored — download them from the export batch.`
                : 'Please import the .csv file into Point Click Care, and then confirm at Closing Confirmation.'}
            </ToastBody>
          </Toast>,
          { intent: missing.length > 0 ? 'warning' : 'success', timeout: missing.length > 0 ? 12000 : 8000 },
        )
        return { appliedCount: invoices_advanced }
      } catch (e) {
        // A refused export carries EVERY problem with the set, not the first —
        // the controller fixes them in one pass rather than one per attempt.
        if (isApiError(e) && e.code === 'validation_failed') {
          const problems = (e.details?.problems as string[] | undefined) ?? []
          throw new Error(
            problems.length > 0
              ? `Nothing was exported. ${problems.length === 1 ? 'One problem' : `${problems.length} problems`} to fix: ${problems.join('; ')}`
              : `Nothing was exported. ${e.message}`,
          )
        }
        throw e
      }
    },
    [dispatchToast],
  )

  return (
    <div className={s.root}>
      <div className={s.header}>
        <ScreenTitle>Export Control</ScreenTitle>
        <Body1 className={s.sub}>For each company with approved invoices, generate a csv file for import to Point Click Care with all line items relating to those invoices.</Body1>
      </div>
      <div className={s.rule} />
      <ApprovalScreen
        session={session}
        refreshNonce={refreshNonce}
        filter={filter}
        onQueuesChanged={onQueuesChanged}
        config={{
          title: '', // the page header above carries the title + subheader
          queueStatus: InvoiceStatus.PendingExportControl,
          step: DecisionStep.ExportControl,
          approveLabel: 'Approve & Export',
          showPdf: true,
          // GL account editable here too (planning §3) — the controller can fix a
          // cost center in place. An uncoded line can never reach Point Click
          // Care: the export validates every line item has an account and refuses
          // the whole set, listing each one.
          //
          // "Who is it for?" on its own as well, per the authority matrix: every
          // approval tier may be unstaffed, so an invoice can arrive here with
          // every cost center null, and the composed picker alone would mean
          // naming a whole account line by line. No standalone "What is it?" —
          // the composed field covers it, and the server refuses it here.
          editAccounts: true,
          editCostCenters: true,
          showTotals: true, // controllers see exactly how much they're authorizing
          groupByCompany: true, // one group per company — the file grain (2026-07-12)
          approveVia,
        }}
      />
    </div>
  )
}
